/**
 * Plugin Bridge Protocol
 *
 * Defines the communication protocol between plugin iframes and the host.
 */

/**
 * Message types for plugin-host communication
 */
export type PluginMessageType =
  | 'ready'
  | 'api_call'
  | 'api_response'
  | 'event'
  | 'props_update'
  | 'error'
  | 'resize'
  // Wave 16: Observability
  | 'plugin_span_start'
  | 'plugin_span_end'
  | 'plugin_log'
  | 'plugin_error'
  // Wave 18c: Hot reload DX
  | 'hot_reload_start'
  | 'hot_reload_success'
  | 'hot_reload_error'
  | 'hot_reload_state_save'
  | 'hot_reload_state_restore'
  | 'hot_reload_state_snapshot'
  | 'hot_reload_retry'
  | 'plugin_toast'
  | 'open_plugin_devtools';

/**
 * Base plugin message structure
 */
export interface PluginMessage {
  type: PluginMessageType;
  pluginId: string;
  requestId?: string;
  timestamp?: number;
}

/**
 * Plugin ready message - sent when plugin iframe is ready
 */
export interface PluginReadyMessage extends PluginMessage {
  type: 'ready';
  version?: string;
}

/**
 * API call message - plugin requests host API
 */
export interface PluginApiCallMessage extends PluginMessage {
  type: 'api_call';
  requestId: string;
  method: string;
  args: any[];
}

/**
 * API response message - host responds to plugin API call
 */
export interface PluginApiResponseMessage extends PluginMessage {
  type: 'api_response';
  requestId: string;
  result?: any;
  error?: string;
}

/**
 * Event message - host sends event to plugin
 */
export interface PluginEventMessage extends PluginMessage {
  type: 'event';
  event: {
    type: string;
    payload: any;
    timestamp: number;
  };
}

/**
 * Props update message - host updates plugin props
 */
export interface PluginPropsUpdateMessage extends PluginMessage {
  type: 'props_update';
  props: Record<string, any>;
}

/**
 * Error message - general error
 */
export interface PluginErrorMessage extends PluginMessage {
  type: 'error';
  error: string;
  code?: string;
}

/**
 * Resize message - plugin requests container resize
 */
export interface PluginResizeMessage extends PluginMessage {
  type: 'resize';
  width?: number;
  height?: number;
}

/**
 * Catch-all for telemetry, observability, hot-reload, and other extension messages
 */
export interface PluginExtensionMessage extends PluginMessage {
  type: Exclude<PluginMessageType, 'ready' | 'api_call' | 'api_response' | 'event' | 'props_update' | 'error' | 'resize'>;
  [key: string]: unknown;
}

/**
 * All message types union
 */
export type AnyPluginMessage =
  | PluginReadyMessage
  | PluginApiCallMessage
  | PluginApiResponseMessage
  | PluginEventMessage
  | PluginPropsUpdateMessage
  | PluginErrorMessage
  | PluginResizeMessage
  | PluginExtensionMessage;

/**
 * Available API methods that plugins can call
 */
export type PluginApiMethod =
  // HTTP API
  | 'api.get'
  | 'api.post'
  | 'api.put'
  | 'api.delete'
  // Config
  | 'config.get'
  | 'config.save'
  // Toast notifications
  | 'toast.success'
  | 'toast.error'
  | 'toast.info'
  // Navigation
  | 'navigate'
  | 'openModal'
  | 'closeModal'
  // Storage
  | 'storage.get'
  | 'storage.set'
  | 'storage.delete'
  // Events
  | 'events.subscribe'
  | 'events.unsubscribe'
  | 'events.publish';

/**
 * Validate a plugin message
 */
export function isValidPluginMessage(data: unknown): data is AnyPluginMessage {
  if (!data || typeof data !== 'object') return false;
  const msg = data as Record<string, unknown>;
  
  if (typeof msg.type !== 'string') return false;
  if (typeof msg.pluginId !== 'string') return false;
  
  const validTypes: string[] = [
    'ready',
    'api_call',
    'api_response',
    'event',
    'props_update',
    'error',
    'resize',
    // Wave 16: Observability
    'plugin_span_start',
    'plugin_span_end',
    'plugin_log',
    'plugin_error',
    // Wave 18c: Hot reload DX
    'hot_reload_start',
    'hot_reload_success',
    'hot_reload_error',
    'hot_reload_state_save',
    'hot_reload_state_restore',
    'hot_reload_state_snapshot',
    'hot_reload_retry',
    'plugin_toast',
    'open_plugin_devtools',
  ];
  
  return validTypes.includes(msg.type as string);
}

/**
 * Create a unique request ID
 */
export function createRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Serialize message for postMessage
 */
export function serializeMessage(msg: AnyPluginMessage): AnyPluginMessage {
  return {
    ...msg,
    timestamp: msg.timestamp || Date.now(),
  };
}
